<template>
  <div class="alice-er-row" :class="{ 'alice-er-row--possible': row.possible }">
    <div class="alice-er-row__year" :title="yearTitle(row)">
      {{ yearLabel(row) }}
    </div>

    <div class="alice-er-row__main">
      <div class="alice-er-row__title-line">
        <span v-if="badge" class="alice-er-row__badge">{{ badge }}</span>
        <router-link v-if="route" :to="route" class="alice-er-row__title">{{
          row.title
        }}</router-link>
        <a
          v-else-if="primary"
          :href="primary.url"
          target="_blank"
          rel="noopener"
          class="alice-er-row__title"
          >{{ row.title }}</a
        >
        <span v-else class="alice-er-row__title alice-er-row__title--plain">{{
          row.title
        }}</span>
      </div>
      <div class="alice-er-row__meta">
        <span class="alice-er-row__source">{{ source }}</span>
        <span v-if="row.client" class="alice-er-row__part">{{
          row.client
        }}</span>
        <span v-if="methods" class="alice-er-row__part"
          ><span :class="{ 'alice-er-row__guess': hint }" :title="hint">{{
            methods
          }}</span></span
        >
        <span v-if="kind" class="alice-er-row__part">{{ kind }}</span>
        <span v-for="t in row.tags || []" :key="t" class="alice-er-row__tag">{{
          t
        }}</span>
        <span v-if="row.nda" class="alice-er-row__tag alice-er-row__tag--nda"
          >NDA</span
        >
      </div>
      <div v-if="detail === 'reason' && row.reason" class="alice-er-row__why">
        {{ row.reason }}
      </div>
      <div v-else-if="row.snippet" class="alice-er-row__why">
        {{ row.snippet }}
      </div>
      <!-- Extra lines under a row: the Drive link prompts of the tab. -->
      <slot />
    </div>

    <div class="alice-er-row__actions">
      <a
        v-for="l in links"
        :key="l.kind"
        :href="l.url"
        target="_blank"
        rel="noopener"
        class="alice-er-row__link"
        :title="`Open ${(LINK_LABELS[l.kind] || l.kind).toLowerCase()}`"
      >
        {{ LINK_LABELS[l.kind] || l.kind }}
        <Icon name="external" :size="10" />
      </a>
      <button
        type="button"
        class="alice-er-row__ask"
        title="Opent de chat met een vraag over dit project. Er wordt niets verstuurd."
        @click="$emit('ask', row)"
      >
        Vraag Alice
      </button>
    </div>
  </div>
</template>

<script>
import Icon from "./Icon.vue";
import {
  KIND_LABELS,
  LINK_LABELS,
  SOURCE_LABELS,
  methodsHint,
  primaryLink,
  rowRoute,
  yearLabel,
  yearTitle,
} from "@/utils/earlierResearch";

// One study in the "Eerder onderzoek" tab: year, title (opens the Alice
// project or the archive file), where it lives, client · methods · kind, why
// it is listed, and its file links.
export default {
  name: "AliceEarlierResearchRow",
  components: { Icon },
  props: {
    row: { type: Object, required: true },
    // A short label before the title ("Ronde 1 van 2", "Zelfde studie").
    badge: { type: String, default: "" },
    // Second line: the catalog's reason ("reason") or the matched passage.
    detail: { type: String, default: "snippet" },
  },
  emits: ["ask"],
  data() {
    return { LINK_LABELS };
  },
  computed: {
    route() {
      return rowRoute(this.row);
    },
    primary() {
      return primaryLink(this.row);
    },
    methods() {
      return Array.isArray(this.row.methods) ? this.row.methods.join(", ") : "";
    },
    kind() {
      return KIND_LABELS[this.row.kind] || "";
    },
    hint() {
      return methodsHint(this.row);
    },
    source() {
      return SOURCE_LABELS[this.row.source] || "";
    },
    links() {
      return (this.row.links || []).filter(
        (l) => l && /^https:\/\//.test(l.url)
      );
    },
  },
  methods: { yearLabel, yearTitle },
};
</script>

<style scoped>
.alice-er-row {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 280px;
  align-items: start;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-er-row:last-child {
  border-bottom: none;
}
.alice-er-row__year {
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--fg-2);
  padding-top: 2px;
}
.alice-er-row__main {
  min-width: 0;
}
.alice-er-row__title-line {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.alice-er-row__badge {
  flex-shrink: 0;
  padding: 2px 6px;
  background: var(--vp-turquoise-20);
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vp-black);
}
.alice-er-row__title {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--vp-black);
  text-decoration: none;
  overflow-wrap: anywhere;
}
a.alice-er-row__title:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.alice-er-row__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin-top: 4px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-er-row__source {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
}
.alice-er-row__part + .alice-er-row__part::before {
  content: "·";
  margin-right: 8px;
}
.alice-er-row__guess {
  text-decoration: underline dotted;
  text-underline-offset: 3px;
  cursor: help;
}
.alice-er-row__tag {
  padding: 1px 6px;
  border: 1px solid var(--border-hairline);
  font-size: 11px;
  color: var(--fg-2);
}
.alice-er-row__tag--nda {
  border-color: var(--vp-rood-100);
  color: var(--vp-rood-120);
}
.alice-er-row__why {
  margin-top: 6px;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.45;
  color: var(--fg-2);
}
.alice-er-row--possible .alice-er-row__why {
  font-style: italic;
}
.alice-er-row__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}
.alice-er-row__link,
.alice-er-row__ask {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  border-radius: 0;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color var(--dur-fast) var(--ease-hover),
    background var(--dur-fast) var(--ease-hover);
}
.alice-er-row__link:hover {
  border-color: var(--vp-black);
}
.alice-er-row__ask:hover {
  background: var(--vp-black);
  border-color: var(--vp-black);
  color: var(--vp-white);
}
.alice-er-row__ask:active,
.alice-er-row__link:active {
  transform: scale(0.98);
}

@media (max-width: 1279px) {
  .alice-er-row {
    grid-template-columns: 48px minmax(0, 1fr) 200px;
  }
}
</style>
